import { Settings, ShowTranslationMode, TextSize } from "./settings";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  PiArrowLeft,
  PiBookmarksSimple,
  PiCheck,
  PiGearSix,
  PiSignOut,
} from "react-icons/pi";
import {
  GENDER_CLASS,
  GENDER_EXAMPLES,
  Gender,
  supportsGenderColoring,
} from "./gender";
import { auth, useUser } from "./firebase";
import { lstr } from "./localization";
import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import getFlagEmoji, { getLanguageName } from "./LanguageFlag";
import { DesktopNav } from "./Navigation";
import { Pup } from "./Pup";
import {
  READ_ALONG_SPEEDS,
  ReadAlong,
  ReadAlongControls,
  formatReadAlongSpeed,
} from "./ReadAlong";

// useMenuDismissal closes an open dropdown menu on a mouse press outside the
// menu's container or on Escape. Escape also moves focus back to the menu's
// toggle button (the container's first button), so keyboard users keep their
// place.
function useMenuDismissal(
  open: boolean,
  setOpen: (open: boolean) => void,
  containerRef: React.RefObject<HTMLDivElement>,
) {
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      containerRef.current?.querySelector("button")?.focus();
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, setOpen, containerRef]);
}

// Scrolled past this many pixels, the reader toolbar shows the story title
// (the page's own title has scrolled away by then) and may hide itself.
const READER_TITLE_SCROLL_PX = 220;

// useScrollState follows the page scroll for the header: whether the page
// is scrolled at all (draws the bottom hairline) and, for the reader, the
// reading progress through the page and whether the toolbar should step
// aside. The toolbar hides while the reader scrolls down into the text and
// comes back on any scroll up, near the top, and at the end of the page.
// While `pinned` (a header menu is open, or the story is being read aloud and
// the page follows the reading by itself) it never hides.
function useScrollState(reader: boolean, pinned: boolean) {
  const [state, setState] = useState({
    scrolled: false,
    pastTitle: false,
    progress: 0,
    hidden: false,
  });
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;

  useEffect(() => {
    let lastY = window.scrollY;
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        reader && scrollable > 0 ? Math.min(1, y / scrollable) : 0;
      const delta = y - lastY;
      setState((previous) => {
        let hidden = previous.hidden;
        if (!reader || pinnedRef.current) hidden = false;
        else if (y < READER_TITLE_SCROLL_PX || progress > 0.98) hidden = false;
        else if (delta > 6) hidden = true;
        else if (delta < -6) hidden = false;
        const next = {
          scrolled: y > 4,
          pastTitle: y > READER_TITLE_SCROLL_PX,
          progress,
          hidden,
        };
        return next.scrolled === previous.scrolled &&
          next.pastTitle === previous.pastTitle &&
          next.hidden === previous.hidden &&
          Math.abs(next.progress - previous.progress) < 0.002
          ? previous
          : next;
      });
      if (Math.abs(delta) > 6) lastY = y;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [reader]);

  return state;
}

// TopMenu is the header of every page. On the main pages it carries the
// logo, the section tabs (wider screens) and the account controls; on story
// pages it becomes a quiet reader toolbar - back, title, listen, reading
// options - with a reading progress line, and slides away while reading.
export function TopMenu({
  reader,
  settings,
  setSettings,
  onOpenSettings,
}: {
  reader?: {
    title: { text: string; lang: string } | null;
    translationAvailable: boolean;
    readAlong: ReadAlong;
  };
  settings: Settings;
  setSettings: (value: Settings) => void;
  onOpenSettings: () => void;
}) {
  const user = useUser();
  const strings = lstr(settings.lLocale);
  // The toolbar stays put while one of its menus is open.
  const [readerMenuOpen, setReaderMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const readingAloud = !!reader && reader.readAlong.status !== "stopped";
  const scroll = useScrollState(
    !!reader,
    readerMenuOpen || userMenuOpen || readingAloud,
  );

  const account = user ? (
    <UserMenu
      user={user}
      locale={settings.lLocale}
      onOpenSettings={onOpenSettings}
      onOpenChange={setUserMenuOpen}
    />
  ) : (
    <>
      {/* On phones both auth buttons don't fit next to the logo once
          translated (de/ru labels are much longer), so only sign-up is
          shown there: most logged-out visitors have no account yet, and
          the sign-up page links to login. */}
      <Link
        to="/login"
        className="btn-ghost hidden h-9 px-3.5 text-sm sm:inline-flex"
      >
        {strings.login_button}
      </Link>
      <Link to="/signup" className="btn-primary h-9 px-4 text-sm">
        {strings.signup_button}
      </Link>
    </>
  );

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b bg-cream/85 backdrop-blur-md transition-[transform,border-color] duration-300 ${
        scroll.scrolled ? "border-border" : "border-transparent"
      } ${scroll.hidden ? "-translate-y-full" : ""}`}
    >
      <div className="flex h-14 items-center gap-1 px-2 sm:gap-2 sm:px-4">
        {reader ? (
          <>
            <BackHomeButton label={strings.reader_back} />
            <div
              lang={reader.title?.lang}
              aria-hidden={!scroll.pastTitle}
              className={`min-w-0 flex-1 truncate px-1 text-center font-literata text-[15px] font-semibold text-main-text transition-opacity duration-300 ${
                scroll.pastTitle && reader.title ? "opacity-100" : "opacity-0"
              }`}
            >
              {reader.title?.text}
            </div>
            <ReadAlongControls
              readAlong={reader.readAlong}
              l={settings.lLocale}
            />
            <ReaderOptionsMenu
              settings={settings}
              setSettings={setSettings}
              translationAvailable={reader.translationAvailable}
              readAlong={reader.readAlong}
              onOpenChange={setReaderMenuOpen}
            />
            {account}
          </>
        ) : (
          <>
            <Link
              to="/"
              aria-label="Polypup"
              className="flex shrink-0 items-center gap-2 rounded-xl px-2 py-1 transition-opacity hover:opacity-80"
            >
              <Pup className="h-8 w-8" />
              <span className="font-literata text-[19px] font-bold tracking-tight text-main-text">
                Polypup
              </span>
            </Link>
            {user && (
              <DesktopNav
                l={settings.lLocale}
                r={settings.rLocale}
                className="ml-3 hidden sm:flex"
              />
            )}
            <div className="ml-auto flex items-center gap-1.5">
              <LanguageButton
                settings={settings}
                onOpenSettings={onOpenSettings}
              />
              {account}
            </div>
          </>
        )}
      </div>
      {reader && (
        // The progress line hangs just below the toolbar, so it stays in
        // view at the top of the screen while the toolbar is hidden.
        <div className="pointer-events-none absolute inset-x-0 top-full h-[2px]">
          <div
            className="h-full bg-primary/80 transition-[width] duration-150"
            style={{ width: `${scroll.progress * 100}%` }}
          />
        </div>
      )}
    </header>
  );
}

// BackHomeButton leaves the story: back through history when the reader
// came from within the app (restoring the list's scroll position), home
// otherwise (e.g. a shared link opened directly).
function BackHomeButton({ label }: { label: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={() =>
        location.key === "default" ? navigate("/") : navigate(-1)
      }
      className="btn-ghost h-10 shrink-0 gap-1.5 rounded-full pl-2 pr-1.5"
    >
      <PiArrowLeft size={20} />
      <Pup className="h-7 w-7" />
    </button>
  );
}

// LanguageButton shows the language being learned and opens the settings,
// where the language pair and the theme are chosen.
function LanguageButton({
  settings,
  onOpenSettings,
}: {
  settings: Settings;
  onOpenSettings: () => void;
}) {
  const strings = lstr(settings.lLocale);
  const label = `${strings.settings_title}: ${getLanguageName(settings.lLocale)} → ${getLanguageName(settings.rLocale)}`;
  return (
    <button
      type="button"
      onClick={onOpenSettings}
      aria-label={label}
      title={label}
      className="btn-ghost h-9 gap-1.5 rounded-full px-2.5 text-sm font-medium text-main-text"
    >
      <span className="text-base leading-none">
        {getFlagEmoji(settings.rLocale)}
      </span>
      <span className="hidden md:inline">
        {getLanguageName(settings.rLocale)}
      </span>
    </button>
  );
}

const popoverClass =
  "animate-pop-in absolute top-full z-50 mt-2 rounded-2xl border border-border bg-surface py-1.5 shadow-pop";

// TranslationMode flattens the (showTranslation, showTranslationMode) pair
// into the single three-way choice the user actually makes. "hidden" keeps
// the stored mode untouched so re-enabling restores the previous preference.
type TranslationMode = "hidden" | "paragraph" | "sentence";

// ReaderOptionsMenu is the "Aa" button in the reader toolbar: a popover with
// everything that affects how the story text is rendered. It is sectioned so
// new reading options can be added without redesigning the header: the text
// size, a translation section (always shown - the mode choices when the
// story has a translation, or a "no translation" note otherwise), and the
// noun-gender coloring toggle plus a color legend previewing the exact tints
// the renderer uses (when the learned language has genders). The "Aa" is
// rendered as serif text rather than an icon - it is the established
// reader-settings affordance and matches the reading typography.
function ReaderOptionsMenu({
  settings,
  setSettings,
  translationAvailable,
  readAlong,
  onOpenChange,
}: {
  settings: Settings;
  setSettings: (value: Settings) => void;
  translationAvailable: boolean;
  readAlong: ReadAlong;
  onOpenChange: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const strings = lstr(settings.lLocale);

  useEffect(() => onOpenChange(open), [open, onOpenChange]);

  // Color legend shown under the gender toggle: previews the exact tint used
  // for each gender next to a familiar example noun in the learned language.
  // undefined for locales without examples (kept defensive: the section is
  // already gated on supportsGenderColoring, but a supported locale could
  // lack examples).
  const genderExamples = GENDER_EXAMPLES[settings.rLocale];
  const genderOrder: Gender[] = ["m", "f", "n"];
  const genderLabel: Record<Gender, string> = {
    m: strings.color_noun_genders_masculine,
    f: strings.color_noun_genders_feminine,
    n: strings.color_noun_genders_neuter,
  };

  useMenuDismissal(open, setOpen, ref);

  const currentMode: TranslationMode = !settings.showTranslation
    ? "hidden"
    : settings.showTranslationMode === ShowTranslationMode.BySentence
      ? "sentence"
      : "paragraph";

  // A mode choice applies right away and keeps the menu open, so the effect
  // is visible in the text behind it.
  const selectMode = (mode: TranslationMode) => {
    setSettings({
      ...settings,
      showTranslation: mode !== "hidden",
      showTranslationMode:
        mode === "sentence"
          ? ShowTranslationMode.BySentence
          : mode === "paragraph"
            ? ShowTranslationMode.ByParagraph
            : settings.showTranslationMode,
    });
  };

  const modeOptions: { mode: TranslationMode; label: string }[] = [
    { mode: "hidden", label: strings.translation_mode_hidden },
    { mode: "paragraph", label: strings.translation_mode_by_paragraph },
    { mode: "sentence", label: strings.translation_mode_by_sentence },
  ];

  const textSizes: { size: TextSize; className: string }[] = [
    { size: TextSize.Small, className: "text-[13px]" },
    { size: TextSize.Medium, className: "text-[17px]" },
    { size: TextSize.Large, className: "text-[21px]" },
  ];
  const textSizeLabel: Record<TextSize, string> = {
    [TextSize.Small]: strings.text_size_small,
    [TextSize.Medium]: strings.text_size_medium,
    [TextSize.Large]: strings.text_size_large,
  };

  const sectionLabelClass = "eyebrow px-4 pb-1.5 pt-2.5";
  const rowClass = (active: boolean) =>
    `w-full flex items-center justify-between gap-3 text-left px-4 py-2 text-sm transition-colors hover:bg-cream-dark ${
      active ? "text-primary font-semibold" : "text-main-text"
    }`;

  // On phones the menu hangs from the header's right edge (the sticky header
  // is its containing block), so it stays on screen when a signed-out
  // reader's Sign up sits to the right of the button.
  return (
    <div className="sm:relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={strings.reader_menu_label}
        title={strings.reader_menu_label}
        aria-expanded={open}
        className={`btn-ghost h-10 w-10 shrink-0 rounded-full ${open ? "bg-cream-dark text-main-text" : ""}`}
      >
        <span className="select-none font-literata text-[18px] font-semibold leading-none">
          Aa
        </span>
      </button>
      {open && (
        <div
          className={`${popoverClass} right-2 w-max min-w-[240px] max-w-[min(300px,calc(100vw-1rem))] origin-top-right sm:right-0`}
        >
          <div className={sectionLabelClass}>{strings.text_size_title}</div>
          <div
            role="radiogroup"
            aria-label={strings.text_size_title}
            className="mx-3 mb-1 grid grid-cols-3 gap-1 rounded-xl bg-cream-dark p-1"
          >
            {textSizes.map(({ size, className }) => {
              const selected = settings.textSize === size;
              return (
                <button
                  key={size}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={textSizeLabel[size]}
                  title={textSizeLabel[size]}
                  onClick={() => setSettings({ ...settings, textSize: size })}
                  className={`flex h-9 items-center justify-center rounded-lg font-literata font-semibold transition-colors ${className} ${
                    selected
                      ? "bg-surface text-main-text shadow-card"
                      : "text-secondary-text hover:text-main-text"
                  }`}
                >
                  A
                </button>
              );
            })}
          </div>
          <div className="my-1.5 border-t border-border" />
          <div className={sectionLabelClass}>{strings.read_along_speed}</div>
          <div
            role="radiogroup"
            aria-label={strings.read_along_speed}
            className="mx-3 mb-1 grid grid-cols-2 gap-1 rounded-xl bg-cream-dark p-1"
          >
            {READ_ALONG_SPEEDS.map((speed) => {
              const selected = readAlong.speed === speed;
              return (
                <button
                  key={speed}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => readAlong.setSpeed(speed)}
                  className={`flex h-9 items-center justify-center rounded-lg text-sm font-semibold tabular-nums transition-colors ${
                    selected
                      ? "bg-surface text-main-text shadow-card"
                      : "text-secondary-text hover:text-main-text"
                  }`}
                >
                  {formatReadAlongSpeed(speed, settings.lLocale)}
                </button>
              );
            })}
          </div>
          <div className="my-1.5 border-t border-border" />
          <div className={sectionLabelClass}>
            {strings.translation_menu_title}
          </div>
          {translationAvailable ? (
            modeOptions.map((option) => {
              const selected = option.mode === currentMode;
              return (
                <button
                  key={option.mode}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onClick={() => selectMode(option.mode)}
                  className={rowClass(selected)}
                >
                  {option.label}
                  {selected && <PiCheck className="text-base" />}
                </button>
              );
            })
          ) : (
            // Story has no translation in the user's language: keep the
            // section visible with an explanatory note rather than dropping
            // it, so the menu's shape stays predictable across stories.
            <p className="px-4 py-2 text-sm text-secondary-text">
              {strings.translation_unavailable}
            </p>
          )}
          {supportsGenderColoring(settings.rLocale) && (
            <>
              <div className="my-1.5 border-t border-border" />
              <div className={sectionLabelClass}>
                {strings.reader_menu_words_section}
              </div>
              {/* A toggle, not a mode choice: clicking flips it and keeps
                  the menu open so the effect is visible in the text. */}
              <button
                type="button"
                role="menuitemcheckbox"
                aria-checked={settings.colorNounGenders}
                onClick={() =>
                  setSettings({
                    ...settings,
                    colorNounGenders: !settings.colorNounGenders,
                  })
                }
                className={rowClass(settings.colorNounGenders)}
              >
                {strings.color_noun_genders_checkbox}
                <span
                  aria-hidden="true"
                  className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                    settings.colorNounGenders ? "bg-primary" : "bg-border"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-surface shadow-card transition-[left] duration-200 ${
                      settings.colorNounGenders ? "left-[18px]" : "left-0.5"
                    }`}
                  />
                </span>
              </button>
              {genderExamples && (
                <div className="flex flex-wrap gap-1.5 px-4 pb-2 pt-1.5">
                  {genderOrder.map((g) => (
                    <div
                      key={g}
                      className="flex items-baseline gap-1.5 rounded-lg border border-border bg-cream px-2.5 py-1"
                    >
                      <span
                        lang={settings.rLocale}
                        className={`font-literata text-sm font-semibold ${GENDER_CLASS[g]}`}
                      >
                        {genderExamples[g]}
                      </span>
                      <span className="text-[11px] uppercase tracking-wide text-secondary-text">
                        {genderLabel[g]}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function UserMenu({
  user,
  locale,
  onOpenSettings,
  onOpenChange,
}: {
  user: User;
  locale: string;
  onOpenSettings: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const strings = lstr(locale);

  useEffect(() => onOpenChange(open), [open, onOpenChange]);

  useMenuDismissal(open, setOpen, ref);

  const photoURL = user.photoURL;
  const initial = (
    user.displayName?.[0] ||
    user.email?.[0] ||
    "?"
  ).toUpperCase();

  const logout = async () => {
    await auth.signOut();
    setOpen(false);
    navigate("/");
  };

  const itemClass =
    "flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-main-text transition-colors hover:bg-cream-dark";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={strings.account_menu_label}
        aria-expanded={open}
        className="ml-0.5 flex h-10 w-10 items-center justify-center rounded-full transition-opacity hover:opacity-80"
      >
        {photoURL ? (
          <img
            src={photoURL}
            alt=""
            referrerPolicy="no-referrer"
            className="h-8 w-8 rounded-full object-cover ring-2 ring-surface"
          />
        ) : (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-on-primary">
            {initial}
          </span>
        )}
      </button>
      {open && (
        <div
          className={`${popoverClass} right-0 min-w-[220px] origin-top-right`}
        >
          <div className="border-b border-border px-4 pb-2.5 pt-1.5">
            {user.displayName && (
              <div className="truncate text-sm font-semibold text-main-text">
                {user.displayName}
              </div>
            )}
            <div className="max-w-[240px] truncate text-xs text-secondary-text">
              {user.email || user.uid}
            </div>
          </div>
          <div className="pt-1">
            <Link
              to="/dictionary"
              onClick={() => setOpen(false)}
              className={itemClass}
            >
              <PiBookmarksSimple size={17} className="text-secondary-text" />
              {strings.my_dictionary_nav}
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onOpenSettings();
              }}
              className={itemClass}
            >
              <PiGearSix size={17} className="text-secondary-text" />
              {strings.settings_title}
            </button>
            <button type="button" onClick={logout} className={itemClass}>
              <PiSignOut size={17} className="text-secondary-text" />
              {strings.logout_button}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
